import { ArrowLeft } from "lucide-react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { addCategory, updateCategory } from "../../../Services/categoryService";
import CustomTextInputBasic from "../../../components/common/CustomTextInput/CustomTextInputBasic";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import FileInput from "../../../components/common/FileInput";
import { toast } from "react-toastify";
import Text from "../../../components/typography/Text";
import CustomSwitch from "../../../components/common/CustomSwitch";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes
const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];

export default function AddCategoryForm({ onCancel, onSave, initialValues = null }) {
  console.log("initialValues", initialValues);
  // Validation Schema
  const validationSchema = Yup.object({
    title: Yup.string()
      .trim()
      .required("Category name is required")
      .min(3, "Category name must be at least 3 characters")
      .max(255, "Category name cannot exceed 255 characters")
      .matches(/^[a-zA-Z0-9\s]*$/, "Special characters are not allowed"),
    description: Yup.string()
      .trim()
      .required("Description is required")
      .min(3, "Description must be at least 3 characters")
      .max(255, "Description cannot exceed 255 characters"),
    file: initialValues ?
    Yup.mixed()
      .optional()
      .test("fileSize", "File size must be less than 2MB", (value) => {
        if (!value) return true;
        if (typeof value === "string") return true;
        return value.size <= MAX_FILE_SIZE;
      })
      .test("fileFormat", "Unsupported file format", (value) => {
        if (!value) return true;
        if (typeof value === "string") return true;
        return SUPPORTED_FORMATS.includes(value.type);
      }) :
    Yup.mixed()
      .required("Logo is required")
      .test("fileSize", "File size must be less than 2MB", (value) => {
        if (!value) return true;
        return value.size <= MAX_FILE_SIZE;
      })
      .test("fileFormat", "Unsupported file format", (value) => {
        if (!value) return true;
        return SUPPORTED_FORMATS.includes(value.type);
      }),
    is_lead_add_without_product: Yup.boolean(),
  });

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      console.log("values", values);
      
      const formData = new FormData();
      formData.append("title", values.title.trim());
      formData.append("description", values.description.trim());
      formData.append("is_lead_add_without_product", values.is_lead_add_without_product);
      if (typeof values.file === "object" && values.file) {
        formData.append("file", values.file);
      }

      let categoryData;
      if (initialValues && initialValues.category_id) {
        formData.append("category_id", initialValues.category_id);
        categoryData = await updateCategory(formData);
        toast.success("Category updated successfully!");
      } else {
        categoryData = await addCategory(formData);
        toast.success("Category added successfully!");
      }
      onSave(categoryData);
    } catch (error) {
      console.error("Error saving category:", error);
      console.log("Error details:", error.response?.data.error.message);
      setErrors({ api: error.response?.data.error.message||"Failed to save category. Try again!" });
      toast.error(error.response?.data.error.message||"Failed to save category. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1">
      {/* Header */}
      <div className="flex items-center gap-6">
        <button className="border p-[9px] rounded-[8px] w-10 h-10" onClick={onCancel}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-semibold">
          {initialValues ? "Update Product Category" : "Add Product Category"}
        </h1>
      </div>

      {/* Formik Form */}
      <Formik
        enableReinitialize
        initialValues={{
          title: initialValues?.category_title || "",
          description: initialValues?.description || "",
          is_lead_add_without_product: initialValues?.is_lead_add_without_product || false,
          file: initialValues?.logo_url || null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, errors, dirty, setFieldValue }) => (
          <Form className="flex-1 p-4 gap-6 border rounded-lg bg-white mt-4">
            <h2 className="text-xl font-semibold mb-4">Basic Details</h2>

            <div className="space-x-4 gap-1 flex justify-between items-center">
              {/* Category Title Field */}
              <div className="w-[45%]">
                <Field name="title">
                  {({ field, meta }) => (
                    <CustomTextInputBasic
                      label="Category Name"
                      {...field}
                      placeholder="Enter"
                      required
                      error={meta.touched && !!meta.error}
                      errorMessage={meta.error}
                    />
                  )}
                </Field>
              </div>

              {/* Description Field */}
              <div className="w-full pr-5">
                <Field name="description">
                  {({ field, meta }) => (
                    <CustomTextInputBasic
                      label="Description"
                      {...field}
                      placeholder="Enter"
                      error={meta.touched && !!meta.error}
                      errorMessage={meta.error}
                    />
                  )}
                </Field>
              </div>
            </div>

            {/* Logo Upload Field */}
            <div className="mt-4">
              {initialValues?.logo_url && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Current Logo</p>
                  <div className="w-20 h-20 border rounded-lg overflow-hidden">
                    <img 
                      src={initialValues.logo_url} 
                      alt="Current Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
              <FileInput
                name="file"
                inputLabel="Category Logo"
                label="Upload Logo"
                accept="image/*"
                size={2}
                sizeVariant="MB"
                setSelectedFiles={(files) => {
                  setFieldValue("file", files[0]);
                }}
                fileType="image"
                fileName={initialValues?.logo_url ? "Current Logo" : null}
              />
            </div>

            <div className="flex items-center gap-4 mt-6">
              <Text className="!font-semibold">Lead Add Without Product</Text>
              <CustomSwitch
                value={values?.is_lead_add_without_product }
                name="is_lead_add_without_product"
                onChange={(checked) => {
                  console.log("checked", checked);
                  // const value = checked ? "true" : "false";
                  const value = checked;
                  setFieldValue("is_lead_add_without_product", value);
                }}
              />
              {/* <span className="text-gray-400 text-xs">Add leads without product</span> */}
            </div>

            {/* API Error Message */}
            {errors.api && <p className="text-red-500 text-sm mt-2">{errors.api}</p>}

            {/* Actions */}
            <div className="p-4 flex justify-end space-x-3">
              <CustomButton label="Cancel" variant="outlined" onClick={onCancel} />
              <CustomButton
                label={initialValues ? "Update" : "Save"}
                variant="contained"
                type="submit"
                disabled={isSubmitting || !dirty}
                className={`${
                  isSubmitting || !dirty ? "!opacity-50 !cursor-not-allowed" : ""
                }`}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
